/**
 * LineChartCard Component
 *
 * Reusable Line Chart with Korat brand colors and responsive design
 * Perfect for trends and time-series data
 */

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

// Korat brand colors (optimized for charts - high contrast)
const COLORS = {
  navy: '#1e3a5f',      // Dark blue
  blue: '#3b82f6',      // Bright blue (เพิ่มความสว่าง)
  orange: '#f97316',    // Vibrant orange
  green: '#22c55e',     // Bright green (เพิ่มความสว่าง)
  red: '#ef4444',       // Red
  purple: '#a855f7',    // Purple
  emerald: '#10b981',   // Emerald (เดิม)
}

export interface LineData {
  [key: string]: string | number
}

export interface LineConfig {
  dataKey: string
  name: string
  color: keyof typeof COLORS
  strokeWidth?: number
  unit?: string  // Optional unit to display in tooltip (e.g., '%', 'คน', 'บาท')
}

export interface LineChartCardProps {
  title: string
  description?: string
  data: LineData[]
  lines: LineConfig[]
  xAxisKey: string
  xAxisLabel?: string
  yAxisLabel?: string
  height?: number
  showGrid?: boolean
  showLegend?: boolean
  isLoading?: boolean
}

export function LineChartCard({
  title,
  description,
  data,
  lines,
  xAxisKey,
  xAxisLabel,
  yAxisLabel,
  height = 350,
  showGrid = true,
  showLegend = true,
  isLoading = false,
}: LineChartCardProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="h-6 bg-slate-200 rounded w-1/3 mb-2 animate-pulse" />
        {description && <div className="h-4 bg-slate-100 rounded w-2/3 mb-4 animate-pulse" />}
        <div className="bg-slate-200 rounded animate-pulse" style={{ height }} />
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="text-base font-semibold text-korat-navy-700">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
        <div className="flex items-center justify-center text-slate-400 text-sm" style={{ height }}>
          ไม่มีข้อมูล
        </div>
      </div>
    )
  }

  // Format large numbers for Y-axis (Thai style)
  const formatYAxis = (value: number) => {
    if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)} พ.ล.`
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} ล้าน`
    if (value >= 100_000) return `${(value / 100_000).toFixed(1)} แสน`
    if (value >= 10_000) return `${(value / 10_000).toFixed(1)} หมื่น`
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)} พัน`
    return new Intl.NumberFormat('th-TH').format(value)
  }

  // Create a map of dataKey to unit for tooltip
  const unitMap = lines.reduce((acc, line) => {
    if (line.unit) {
      acc[line.dataKey] = line.unit
    }
    return acc
  }, {} as Record<string, string>)

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null

    return (
      <div className="bg-white border border-slate-300 rounded-lg shadow-xl p-4">
        <p className="text-sm font-bold text-korat-navy-700 mb-3 border-b border-slate-200 pb-2">
          {label}
        </p>
        {payload.map((entry: any, index: number) => {
          const unit = unitMap[entry.dataKey] || ''
          const formattedValue = entry.value % 1 === 0
            ? new Intl.NumberFormat('th-TH').format(entry.value)
            : entry.value.toFixed(2)

          return (
            <div key={index} className="flex items-center justify-between gap-4 text-sm py-1">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-slate-700">{entry.name}:</span>
              </div>
              <span className="font-bold tabular-nums text-korat-navy-700">
                {formattedValue}{unit}
              </span>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-base font-semibold text-korat-navy-700">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-1 leading-relaxed">{description}</p>}
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}

          <XAxis
            dataKey={xAxisKey}
            tick={{ fontSize: 13, fill: '#334155', fontWeight: 500 }}
            stroke="#94a3b8"
            label={
              xAxisLabel
                ? { value: xAxisLabel, position: 'insideBottom', offset: -5, style: { fontSize: 13 } }
                : undefined
            }
          />

          <YAxis
            tickFormatter={formatYAxis}
            tick={{ fontSize: 13, fill: '#64748b', fontWeight: 500 }}
            stroke="#94a3b8"
            label={
              yAxisLabel
                ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 13 } }
                : undefined
            }
          />

          <Tooltip content={<CustomTooltip />} />

          {showLegend && (
            <Legend
              wrapperStyle={{ fontSize: 13, fontWeight: 500 }}
              iconType="line"
            />
          )}

          {lines.map((line, index) => (
            <Line
              key={index}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={COLORS[line.color]}
              strokeWidth={line.strokeWidth || 3}
              dot={{ fill: COLORS[line.color], r: 4 }}
              activeDot={{ r: 6 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
